import type { MessageType } from "@univdiam/ui/type"

/**
 * Identity for an outgoing message, generated before it is sent.
 *
 * crypto.randomUUID is only exposed in secure contexts, so it is absent when
 * the app is served over plain http (a LAN IP during development, for
 * instance). Falling back keeps sending working there; the fallback only needs
 * to be unique among one user's in-flight messages, not globally unguessable.
 */
export const newClientMessageId = (): string => {
  const c = globalThis.crypto
  if (c && typeof c.randomUUID === "function") return c.randomUUID()
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}

/**
 * Reconciling one conversation from three arrivals of the same message.
 *
 * A sent message can reach the UI three ways, in any order:
 *
 *   1. the optimistic copy the sender adds before the network is touched
 *   2. the socket echo, once the server has stored it
 *   3. a paginated re-fetch, which returns it from the database
 *
 * Before client_msg_id there was no way to tell these were the same message
 * until (3) gave it an id, so the client matched on (sender_id, message-text).
 * That is not identity: it breaks on two identical messages, on media-only
 * messages whose text is empty, and on any server-side normalisation of the
 * HTML. The result was duplicates, and messages that vanished when local state
 * was reset.
 *
 * These helpers are pure and dependency-free on purpose. The wiring differs
 * between the admin and customer apps, but the reconciliation must not -- it
 * was duplicated in both before, and only one copy ever got fixed.
 */

/** A message identity, preferring the server's id and falling back to the client's. */
const identityOf = (m: MessageType): string | null => {
  if (m.id !== undefined && m.id !== null && String(m.id) !== "") return `id:${String(m.id)}`
  if (m.client_msg_id) return `cid:${m.client_msg_id}`
  return null
}

/**
 * Collapse a list so each message appears once.
 *
 * The FIRST occurrence wins, which is what callers want: server-derived rows
 * are passed ahead of optimistic ones, so the authoritative copy (real id, DB
 * created_at, resolved sender_name) is the one kept.
 *
 * Messages with no id and no client_msg_id cannot be identified -- they predate
 * client_msg_id, or came from an older client -- so they are passed through
 * untouched rather than silently collapsed into each other.
 */
export function dedupeMessages(messages: MessageType[]): MessageType[] {
  const seenId = new Set<string>()
  const seenClientId = new Set<string>()
  const out: MessageType[] = []

  for (const m of messages) {
    const id = m.id !== undefined && m.id !== null && String(m.id) !== "" ? String(m.id) : null
    const cid = m.client_msg_id || null

    if (id && seenId.has(id)) continue
    if (cid && seenClientId.has(cid)) continue

    if (id) seenId.add(id)
    if (cid) seenClientId.add(cid)
    out.push(m)
  }

  return out
}

/**
 * The list to render, assembled from three sources.
 *
 *   paged    -- the paginated query cache. Authoritative, but it is a WINDOW:
 *               page 0 always means "the newest N", so it cannot be grown.
 *   live     -- messages the server confirmed over the socket during this
 *               session, held outside the cache.
 *   pending  -- sends not yet acknowledged.
 *
 * Why `live` is kept separate rather than written into the paginated cache:
 * appending to page 0 grows it past the server's page size, and the next
 * refetch snaps it back to N rows. Anything pushed out of that window then
 * falls into the gap between page 0's new boundary and page 1's frozen cursor
 * -- still in the database, invisible in the UI. That is a message
 * disappearing, and it was reproduced against real data before this was
 * changed.
 *
 * Nothing is lost by keeping `live` in component state: every message in it is
 * already stored server-side, so a remount or refetch brings it back through
 * `paged`. Only `pending` represents state the server does not have, and that
 * is exactly the state a failed send must keep in order to offer a retry.
 *
 * Confirmed messages are sorted by id, which is monotonic and agrees with
 * created_at (createOrderChat stamps it at insert). That makes the result
 * independent of which source a message arrived from, or in what order.
 */
export function mergeChatMessages(
  pagedMessages: MessageType[],
  liveMessages: MessageType[],
  pendingMessages: MessageType[] = []
): MessageType[] {
  // paged first, so on a tie the authoritative server copy is the one kept.
  const confirmed = dedupeMessages([...pagedMessages, ...liveMessages])

  const sortKey = (m: MessageType): number => {
    const n = Number(m.id)
    // Messages with no usable id keep their relative position at the end
    // rather than sorting to the front.
    return Number.isFinite(n) ? n : Number.MAX_SAFE_INTEGER
  }
  confirmed.sort((a, b) => sortKey(a) - sortKey(b))

  const confirmedClientIds = new Set(
    confirmed.map(m => m.client_msg_id).filter(Boolean) as string[]
  )

  const stillPending = pendingMessages.filter(
    p => !p.client_msg_id || !confirmedClientIds.has(p.client_msg_id)
  )

  return [...confirmed, ...stillPending]
}

/**
 * Fold a batch of messages into an accumulated list.
 *
 * Callers use this to keep every message they have ever seen, because the
 * paginated cache is a moving window that can SHRINK. Page 0 means "the newest
 * N": once new messages exist, refetching it returns a different N rows and the
 * displaced ones are dropped from the cache while page 1's cursor stays frozen
 * where it was. Those messages are still in the database but no longer reachable
 * from the cache -- they vanish from the conversation mid-session.
 *
 * Accumulating makes the rendered list monotonic: it only ever gains messages.
 * Updates still apply, because upsertMessage replaces a message matched by id
 * (an attention flag cleared by the modal, for instance, arrives as a
 * replacement rather than a duplicate).
 *
 * Returns the original reference when nothing changed, so React callers can
 * skip a state update entirely.
 */
export function upsertMessages(
  messages: MessageType[],
  incoming: MessageType[]
): MessageType[] {
  let next = messages
  for (const m of incoming) next = upsertMessage(next, m)
  return next
}

/**
 * Insert a message, or replace the copy already there.
 *
 * Used for both the socket echo and the send acknowledgement, which may arrive
 * in either order and may both carry the same message. Matching on id OR
 * client_msg_id means an echo can land on top of an optimistic row that has no
 * id yet, which is the case that used to produce a duplicate.
 *
 * Returns the original array reference when nothing changed, so React callers
 * can skip a re-render.
 */
export function upsertMessage(messages: MessageType[], incoming: MessageType): MessageType[] {
  const incomingIdentity = identityOf(incoming)

  const index = messages.findIndex(m => {
    if (
      incoming.id !== undefined && incoming.id !== null &&
      m.id !== undefined && m.id !== null &&
      String(m.id) === String(incoming.id)
    ) return true
    if (incoming.client_msg_id && m.client_msg_id === incoming.client_msg_id) return true
    return false
  })

  if (index === -1) {
    // Unidentifiable messages still append; they simply cannot be matched later.
    if (!incomingIdentity && messages.includes(incoming)) return messages
    return [...messages, incoming]
  }

  const existing = messages[index]
  if (existing === incoming) return messages

  const next = [...messages]
  next[index] = incoming
  return next
}
